import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserContext } from '../userContext';
import { useUserLimitDispatch } from '../user_limit_context';
import { supabase } from '../../services/supabaseClient';
import { extractPlainTextFromTipTap } from '../../utils/tiptapPlainText';

const NoteStateContext = createContext(undefined);
const NoteDispatchContext = createContext(undefined);

const ALLOWED_SOURCE_TYPES = ['audio', 'pdf', 'image', 'doc', 'docx', 'link', 'youtube'];
const ALLOWED_NOTE_TYPES = ['visual', 'kinesthic', 'audio', 'read'];
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const DEBUG_NOTE_PIPELINE = (() => {
	const envEnabled = String(process.env.REACT_APP_DEBUG_NOTE_PIPELINE || '').toLowerCase() === 'true';
	if (typeof window === 'undefined') {
		return envEnabled;
	}

	try {
		return envEnabled || String(window.localStorage.getItem('DEBUG_NOTE_PIPELINE') || '').toLowerCase() === 'true';
	} catch (error) {
		return envEnabled;
	}
})();
const ALLOWED_SOURCE_FILE_MIME_TYPES = new Set([
	'audio/mpeg',
	'audio/mp3',
	'audio/wav',
	'audio/x-wav',
	'audio/mp4',
	'audio/x-m4a',
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	// Image types
	'image/png',
	'image/jpeg',
	'image/jpg',
	'image/webp',
	'image/gif',
	'image/bmp',
	'image/svg+xml',
]);

const getFileExtension = (fileName = '') => fileName.toLowerCase().split('.').pop();

const getSourceTypeFromFile = (file) => {
	if (!file) return null;

	const extension = getFileExtension(file.name);
	const mimeType = file.type;

	if (mimeType.startsWith('audio/') || ['mp3', 'wav', 'm4a'].includes(extension)) {
		return 'audio';
	}

	if (mimeType === 'application/pdf' || extension === 'pdf') {
		return 'pdf';
	}

	// Image detection
	if (mimeType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'].includes(extension)) {
		return 'image';
	}

	if (mimeType === 'application/msword' || extension === 'doc') {
		return 'doc';
	}

	if (
		mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
		extension === 'docx'
	) {
		return 'docx';
	}

	return null;
};

const isAllowedSourceFile = (file) => {
	if (!file) return false;

	const extension = getFileExtension(file.name);
	const hasAllowedMimeType = ALLOWED_SOURCE_FILE_MIME_TYPES.has(file.type);
	const hasAllowedExtension = ['mp3', 'wav', 'm4a', 'pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'].includes(extension);

	return hasAllowedMimeType || hasAllowedExtension;
};

const createEmptyTipTapDoc = () => ({
	type: 'doc',
	content: [{ type: 'paragraph' }],
});

const createEmptyMindMapTitles = () => [];

const createEmptyMindMapsJson = () => [];

const createEmptyAudioBlockTitles = () => [];

const createEmptyAudioBlockJson = () => [];

const hasNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;

const hasCachedAdaptedContent = ({
	noteType,
	selectedMode,
	visualMindMapTitles,
	visualMindMapsJson,
	visualMindMapTitlesSummarized,
	visualMindMapsJsonSummarized,
	audioBlockTitles,
	audioBlockJson,
	audioBlockTitlesSummarized,
	audioBlockJsonSummarized,
	list_content_smart_text,
	list_title_smart_text,
	list_content_smart_textSummarized,
	list_title_smart_textSummarized,
	listJson_content_flashCards,
	listJson_content_flashCardsSummarized,
}) => {
	if (noteType === 'read') {
		if (selectedMode === 'summarized') {
			return hasNonEmptyArray(list_content_smart_textSummarized) || hasNonEmptyArray(list_title_smart_textSummarized);
		}
		return hasNonEmptyArray(list_content_smart_text) || hasNonEmptyArray(list_title_smart_text);
	}

	if (noteType === 'kinesthic') {
		return selectedMode === 'summarized'
			? hasNonEmptyArray(listJson_content_flashCardsSummarized)
			: hasNonEmptyArray(listJson_content_flashCards);
	}

	if (noteType === 'audio') {
		if (selectedMode === 'summarized') {
			return hasNonEmptyArray(audioBlockTitlesSummarized) || hasNonEmptyArray(audioBlockJsonSummarized);
		}
		return hasNonEmptyArray(audioBlockTitles) || hasNonEmptyArray(audioBlockJson);
	}

	if (selectedMode === 'summarized') {
		return hasNonEmptyArray(visualMindMapTitlesSummarized) || hasNonEmptyArray(visualMindMapsJsonSummarized);
	}
	return hasNonEmptyArray(visualMindMapTitles) || hasNonEmptyArray(visualMindMapsJson);
};

const buildErrorMessage = (payload, status, fallback) => String(
	payload?.error || payload?.message || `${fallback} (${status})`
);

const stableStringify = (value) => {
	if (value === null || value === undefined) return String(value);
	if (typeof value !== 'object') return JSON.stringify(value);
	if (Array.isArray(value)) {
		return `[${value.map((item) => stableStringify(item)).join(',')}]`;
	}

	const keys = Object.keys(value).sort();
	return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
};

const cloneTipTapJson = (value) => {
	if (!value || typeof value !== 'object') return value;
	try {
		return JSON.parse(JSON.stringify(value));
	} catch {
		return value;
	}
};

const toFileArray = (filesOrFile) => {
	if (!filesOrFile) return [];
	if (typeof FileList !== 'undefined' && filesOrFile instanceof FileList) {
		return Array.from(filesOrFile);
	}
	return Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
};

const normalizeEditorDoc = (value) => {
	let current = value;

	for (let depth = 0; depth < 4; depth += 1) {
		if (!current) return null;

		if (typeof current === 'string') {
			try {
				current = JSON.parse(current);
				continue;
			} catch {
				return null;
			}
		}

		if (typeof current !== 'object') {
			return null;
		}

		if (current.type === 'doc' && Array.isArray(current.content)) {
			return cloneTipTapJson(current);
		}

		if (current.EditorContent_json_tiptap !== undefined) {
			current = current.EditorContent_json_tiptap;
			continue;
		}

		if (current.editorJsonContent !== undefined) {
			current = current.editorJsonContent;
			continue;
		}

		if (current.editor_json_content !== undefined) {
			current = current.editor_json_content;
			continue;
		}

		return null;
	}

	return null;
};

const mergeTipTapDocs = (docs = []) => {
	const mergedContent = [];

	docs.forEach((doc) => {
		const normalizedDoc = normalizeEditorDoc(doc);
		if (!normalizedDoc || !Array.isArray(normalizedDoc.content) || normalizedDoc.content.length === 0) {
			return;
		}

		if (mergedContent.length > 0) {
			mergedContent.push({ type: 'paragraph' });
		}

		mergedContent.push(...cloneTipTapJson(normalizedDoc.content));
	});

	return mergedContent.length > 0
		? { type: 'doc', content: mergedContent }
		: createEmptyTipTapDoc();
};

const createTipTapDocFromText = (text = '') => {
	const normalizedText = String(text || '').trim();
	if (!normalizedText) {
		return createEmptyTipTapDoc();
	}

	return {
		type: 'doc',
		content: [
			{
				type: 'paragraph',
				content: [{ type: 'text', text: normalizedText }],
			},
		],
	};
};

const normalizeChatConversation = (value) => {
	if (Array.isArray(value)) return value;
	if (!value || typeof value !== 'object') return [];

	const candidateKeys = [
		'chat_conversation_json',
		'messages',
		'conversation',
		'chat',
		'history',
		'turns',
	];

	for (const key of candidateKeys) {
		if (Array.isArray(value[key])) {
			return value[key];
		}
	}

	return [];
};

const buildNoteSaveSignature = (payload) => {
	if (!payload) return '';
	const { showExitSavingOverlay: _ignoredExitSavingOverlay, ...persistablePayload } = payload;
	return stableStringify({
		...persistablePayload,
		chat_conversation_json: normalizeChatConversation(persistablePayload.chat_conversation_json),
	});
};

const isUuidLike = (value) => {
	const raw = String(value || '').trim();
	return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(raw);
};

const createClientNoteId = () => {
	try {
		if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
			return window.crypto.randomUUID();
		}
	} catch (error) {
		// Fallback below.
	}

	const randomPart = Math.random().toString(16).slice(2, 10).padEnd(8, '0');
	const timePart = Date.now().toString(16).slice(-12).padStart(12, '0');
	return `${randomPart.slice(0, 8)}-${timePart.slice(0, 4)}-4${timePart.slice(4, 7)}-a${timePart.slice(7, 10)}-${timePart.slice(0, 12)}`;
};

const normalizeNoteType = (value) => {
	const normalized = String(value || '').toLowerCase();
	return ALLOWED_NOTE_TYPES.includes(normalized) ? normalized : 'visual';
};

const getNoteRouteStateFromPathname = (pathname) => {
	const value = String(pathname || '').trim();
	const match = value.match(/^\/note-(editor|adapted)(?:\/([^/?#]+))?(?:\/([^/?#]+))?/i);
	if (!match) {
		return {
			view: null,
			noteId: null,
			noteType: null,
		};
	}

	const view = String(match[1] || '').toLowerCase() === 'adapted' ? 'adapted' : 'editor';
	const noteId = match[2] ? decodeURIComponent(match[2]) : null;
	const noteType = view === 'adapted' ? normalizeNoteType(match[3]) : null;

	return {
		view,
		noteId,
		noteType,
	};
};

const getNoteIdFromPathname = (pathname) => {
	return getNoteRouteStateFromPathname(pathname).noteId;
};

const getNotePathForView = (view, id, noteType = 'visual') => {
	const normalizedView = String(view || 'editor').toLowerCase() === 'adapted' ? 'adapted' : 'editor';
	const sanitizedId = String(id || '').trim();
	if (!sanitizedId) {
		return normalizedView === 'adapted' ? '/note-adapted' : '/note-editor';
	}

	return normalizedView === 'adapted'
		? `/note-adapted/${encodeURIComponent(sanitizedId)}/${encodeURIComponent(normalizeNoteType(noteType))}`
		: `/note-editor/${encodeURIComponent(sanitizedId)}`;
};

let lastAdaptationSignature = null;



export const NoteProvider = ({ children }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		user_id,
		access_token,
		refresh_token,
		session_expires_at,
		setConnectionState,
		setUser_id,
		resetUserState,
	} = useUserContext();
	const {
		incrementContentUploaded,
		incrementAdaptionsContent,
	} = useUserLimitDispatch();

	const [EditorContent_json_tiptap, setEditorContent_json_tiptap] = useState(null);
	const [note_id, setNote_id] = useState(null);
	const [visualMindMapTitles, setVisualMindMapTitles] = useState(null);
	const [visualMindMapsJson, setVisualMindMapsJson] = useState(null);
	const [name_note, setName_note] = useState('');
	const [if_note_opened, setIf_note_opened] = useState(false);
	const [if_expect_source_content_parsing, setIf_expect_source_content_parsing] = useState(false); // for editor loading state
	const [if_source_content_parsing_finished, setIf_source_content_parsing_finished] = useState(false); // for editor loading state
	const [if_loading_note_content, setIf_loading_note_content] = useState(false); // for route-driven note hydration loading state
	const [if_saving_note_content, setIf_saving_note_content] = useState(false); // for exit/navigation save loading state
	const [if_saving_note_content_on_exit, setIf_saving_note_content_on_exit] = useState(false); // only for leave-note overlay
	const [if_expect_note_content_adapting, setIf_expect_note_content_adapting] = useState(false); // for adapted mindmap loading state
	const [if_note_content_adapting_finish, setIf_note_content_adapting_finish] = useState(false); // for adapted mindmap loading state
	const [note_adapting_run_keys, setNote_adapting_run_keys] = useState([]);
	const [if_note_summarized_or_deep, setIf_note_summarized_or_deep] = useState('deep');
	const [current_note_view, setCurrent_note_view_state] = useState('editor');
	const [current_note_type, setCurrent_note_type_state] = useState('visual'); // visual or audio
	const [audioBlockTitles, setAudioBlockTitles] = useState(null);
	const [audioBlockJson, setAudioBlockJson] = useState(null);
	const [list_content_smart_text, setList_content_smart_text] = useState(null);
	const [list_title_smart_text, setList_title_smart_text] = useState(null);
	const [listJson_content_flashCards, setListJson_content_flashCards] = useState(null);
	const [visualMindMapTitlesSummarized, setVisualMindMapTitlesSummarized] = useState(null);
	const [visualMindMapsJsonSummarized, setVisualMindMapsJsonSummarized] = useState(null);
	const [audioBlockTitlesSummarized, setAudioBlockTitlesSummarized] = useState(null);
	const [audioBlockJsonSummarized, setAudioBlockJsonSummarized] = useState(null);
	const [list_content_smart_textSummarized, setList_content_smart_textSummarized] = useState(null);
	const [list_title_smart_textSummarized, setList_title_smart_textSummarized] = useState([null]);
	const [listJson_content_flashCardsSummarized, setListJson_content_flashCardsSummarized] = useState(null);
	const [note_source_type, setNote_source_type] = useState(null);
	const [note_source_form_data, setNote_source_form_data] = useState(() => new FormData());
	const [chat_conversation_json, setChat_conversation_json] = useState([]);
	const latestEditorContentRef = useRef(EditorContent_json_tiptap);
	const note_source_form_data_ref = useRef(note_source_form_data);
	const adaptRunRef = useRef({
		inFlightByKey: new Map(),
		lastCompletedSourceByKey: new Map(),
	});
	const lastGeneratedSignatureByRunKeyRef = useRef(new Map());
	const lastLoadedNoteIdRef = useRef(null);
	const currentPathnameRef = useRef(location.pathname);
	const previousPathnameRef = useRef(location.pathname);
	const hasMountedPathTrackingRef = useRef(false);
	const currentNoteIdRef = useRef(note_id);
	const currentNoteViewRef = useRef(current_note_view);
	const currentNoteTypeRef = useRef(current_note_type);
	const navigationIntentRef = useRef(0);
	const loadingNoteIdRef = useRef(null);
	const creatingNoteIdRef = useRef(false);
	const bootstrappingImportedNoteRef = useRef(false);
	const noteAutoSaveTimerRef = useRef(null);
	const lastSavedPayloadSignatureRef = useRef('');
	const accessTokenRef = useRef(access_token);
	const editorContentGetterRef = useRef(null);
	const buildCurrentNoteSavePayloadRef = useRef(null);

	const bumpNavigationIntent = useCallback(() => {
		navigationIntentRef.current += 1;
		return navigationIntentRef.current;
	}, []);

	const setCurrent_note_view = useCallback((nextView) => {
		const normalizedView = String(nextView || 'editor').toLowerCase() === 'adapted' ? 'adapted' : 'editor';
		setCurrent_note_view_state((previousView) => {
			if (previousView === normalizedView) {
				return previousView;
			}
			bumpNavigationIntent();
			return normalizedView;
		});
	}, [bumpNavigationIntent]);

	const setCurrent_note_type = useCallback((nextType) => {
		const normalizedType = nextType === null ? null : normalizeNoteType(nextType);
		setCurrent_note_type_state((previousType) => {
			if (previousType === normalizedType) {
				return previousType;
			}
			bumpNavigationIntent();
			return normalizedType;
		});
	}, [bumpNavigationIntent]);

	useEffect(() => {
		note_source_form_data_ref.current = note_source_form_data;
	}, [note_source_form_data]);

	useEffect(() => {
		latestEditorContentRef.current = EditorContent_json_tiptap;
	}, [EditorContent_json_tiptap]);

	useEffect(() => {
		currentPathnameRef.current = location.pathname;
	}, [location.pathname]);

	useEffect(() => {
		currentNoteIdRef.current = note_id;
	}, [note_id]);

	useEffect(() => {
		currentNoteViewRef.current = current_note_view;
	}, [current_note_view]);

	useEffect(() => {
		accessTokenRef.current = access_token;
	}, [access_token]);

	useEffect(() => {
		currentNoteTypeRef.current = current_note_type;
	}, [current_note_type]);

	const setEditorContentJsonSync = useCallback((nextContent) => {
		// Just update state; the useEffect will sync to the ref
		setEditorContent_json_tiptap(cloneTipTapJson(nextContent));
	}, []);

	const registerEditorContentGetter = useCallback((getter) => {
		editorContentGetterRef.current = getter;
	}, []);

	const setNoteSourceType = useCallback((sourceType) => {
		if (sourceType === null || ALLOWED_SOURCE_TYPES.includes(sourceType)) {
			setNote_source_type(sourceType);
		}
	}, []);

	const getEffectiveAuth = useCallback(async () => {
		const nowSeconds = Math.floor(Date.now() / 1000);
		const parseExpiresAtSeconds = (value) => {
			if (value === null || value === undefined || value === '') {
				return null;
			}
			const asNumber = Number(value);
			if (Number.isFinite(asNumber)) {
				return asNumber;
			}
			const parsedMs = Date.parse(String(value));
			if (Number.isFinite(parsedMs)) {
				return Math.floor(parsedMs / 1000);
			}
			return null;
		};
		const expiresAtSeconds = parseExpiresAtSeconds(session_expires_at);
		const hasFreshToken = Boolean(access_token)
			&& (!session_expires_at || (Number.isFinite(expiresAtSeconds) && expiresAtSeconds > nowSeconds + 30));
		if (hasFreshToken) {
			return {
				accessToken: access_token,
				userId: user_id || null,
			};
		}

		if (!supabase) {
			return null;
		}

		try {
			const { data: sessionData } = await supabase.auth.getSession();
			const session = sessionData?.session;
			if (session?.access_token) {
				setConnectionState({
					is_authenticated: true,
					access_token: session.access_token,
					refresh_token: session.refresh_token || '',
					session_expires_at: session.expires_at || null,
					connection_status: 'connected',
					connection_error: '',
				});
				if (session?.user?.id) {
					setUser_id(session.user.id);
				}
				return {
					accessToken: session.access_token,
					userId: session?.user?.id || user_id || null,
				};
			}
		} catch (error) {
			// Continue to refresh fallback.
		}

		if (!refresh_token) {
			return null;
		}

		try {
			const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession({ refresh_token });
			if (refreshError || !refreshData?.session?.access_token) {
				return null;
			}

			const refreshedSession = refreshData.session;
			setConnectionState({
				is_authenticated: true,
				access_token: refreshedSession.access_token,
				refresh_token: refreshedSession.refresh_token || '',
				session_expires_at: refreshedSession.expires_at || null,
				connection_status: 'connected',
				connection_error: '',
			});
			if (refreshedSession?.user?.id) {
				setUser_id(refreshedSession.user.id);
			}

			return {
				accessToken: refreshedSession.access_token,
				userId: refreshedSession?.user?.id || user_id || null,
			};
		} catch (error) {
			return null;
		}
	}, [access_token, refresh_token, session_expires_at, setConnectionState, setUser_id, user_id]);

	const handleAuthFailure = useCallback((reason = 'Session expired') => {
		resetUserState();
		navigate('/connection', { replace: true });
	}, [resetUserState, navigate]);

	const initNewNoteSettings = useCallback(async (sourceType) => {
		const normalizedSourceType = sourceType === 'youtube' ? 'link' : sourceType;
		setNoteSourceType(sourceType);
		const currentSourceFormData = note_source_form_data_ref.current;

		if (normalizedSourceType === null) {
			const emptyDoc = createEmptyTipTapDoc();
			setNote_id(null);
			setEditorContentJsonSync(emptyDoc);
			setIf_source_content_parsing_finished(true);
			setIf_expect_source_content_parsing(false);
			return emptyDoc;
		}

		if (!['pdf', 'image', 'doc', 'link', 'audio'].includes(normalizedSourceType)) {
			throw new Error(`sourceType '${String(sourceType)}' is not supported by initNewNoteSettings.`);
		}

		setIf_expect_source_content_parsing(true);
		setIf_source_content_parsing_finished(false);
		bootstrappingImportedNoteRef.current = true;

		try {
			let response;
			let payload;

			if (['pdf', 'image', 'doc'].includes(normalizedSourceType)) {
				const sourceFiles = toFileArray(currentSourceFormData.getAll('source_file')).filter((file) => file instanceof File);
				if (sourceFiles.length === 0) {
					throw new Error(`No ${normalizedSourceType} file found in note_source_form_data.`);
				}

				const formData = new FormData();
				// server route expects files in field 'pdf' — keep compatibility and let server detect file type
				sourceFiles.forEach((sourceFile) => {
					formData.append('pdf', sourceFile, sourceFile.name || 'document.pdf');
				});

				// pass a hint to backend about original source type
				formData.append('extraFields', JSON.stringify({ source_type: normalizedSourceType }));

				// Step 1: start the job (fast — just creates MinnerU batch and uploads the file)
				const startResponse = await fetch(`${BACKEND_BASE_URL}/api/ai/pdf/start`, {
					method: 'POST',
					body: formData,
				});
				const startPayload = await startResponse.json().catch(() => ({}));
				if (!startResponse.ok || !startPayload?.batchId) {
					throw new Error(buildErrorMessage(startPayload, startResponse.status, `Failed to build TipTap from ${normalizedSourceType}`));
				}
				const { batchId } = startPayload;

				// Step 2: poll until MinnerU finishes (each call is fast, stays under Vercel proxy timeout)
				const MAX_POLL_ATTEMPTS = 150; // 150 × 2s = 5 min max
				const POLL_INTERVAL_MS = 2000;
				let tiptapDoc = null;
				for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
					const pollResponse = await fetch(`${BACKEND_BASE_URL}/api/ai/pdf/poll/${batchId}`);
					const pollPayload = await pollResponse.json().catch(() => ({}));
					if (!pollResponse.ok) {
						throw new Error(buildErrorMessage(pollPayload, pollResponse.status, `Failed to build TipTap from ${normalizedSourceType}`));
					}
					if (pollPayload?.state === 'done') {
						tiptapDoc = pollPayload.tiptapDoc;
						break;
					}
					if (pollPayload?.state === 'failed') {
						throw new Error(pollPayload?.error || `Failed to build TipTap from ${normalizedSourceType}`);
					}
					await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
				}

				if (!tiptapDoc) {
					throw new Error(`Failed to build TipTap from ${normalizedSourceType}: timed out waiting for MinnerU`);
				}
				payload = { data: tiptapDoc };
			}

			if (normalizedSourceType === 'audio') {
				const sourceFiles = toFileArray(currentSourceFormData.getAll('source_file')).filter((file) => file instanceof File);
				if (sourceFiles.length === 0) {
					throw new Error('No audio file found in note_source_form_data.');
				}

				const formData = new FormData();
				sourceFiles.forEach((sourceFile) => {
					formData.append('audio', sourceFile, sourceFile.name || 'audio');
				});

				response = await fetch(`${BACKEND_BASE_URL}/api/ai/audio/tiptap`, {
					method: 'POST',
					body: formData,
				});

				payload = await response.json().catch(() => ({}));
				if (!response.ok || !payload?.data) {
					throw new Error(buildErrorMessage(payload, response.status, 'Failed to build TipTap from audio'));
				}
			}

			if (normalizedSourceType === 'link') {
				const link = String(currentSourceFormData.get('link') || '').trim();
				if (!link) {
					throw new Error('No link found in note_source_form_data.');
				}

				response = await fetch(`${BACKEND_BASE_URL}/api/ai/link/tiptap`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ url: link }),
				});

				payload = await response.json().catch(() => ({}));
				if (!response.ok || !payload?.data) {
					throw new Error(buildErrorMessage(payload, response.status, 'Failed to build TipTap from link'));
				}
			}

			const tiptapDoc = payload?.data;
			const provisionalNoteId = createClientNoteId();
			setNote_id(provisionalNoteId);
			setEditorContentJsonSync(tiptapDoc);
			setIf_source_content_parsing_finished(true);
			setIf_note_opened(true);
			setCurrent_note_view('editor');
			incrementContentUploaded();
			const provisionalPath = getNotePathForView('editor', provisionalNoteId, current_note_type);
			if (currentPathnameRef.current !== provisionalPath) {
				navigate(provisionalPath, { replace: true });
			}

			const auth = await getEffectiveAuth();
			if (!auth?.accessToken) {
				handleAuthFailure('Session expired. Please sign in again.');
				throw new Error('Authentication required to save note content.');
			}
			if (tiptapDoc && typeof tiptapDoc === 'object') {
				creatingNoteIdRef.current = true;
				try {
					const persistedNote = await saveNoteContentToDatabase({
						noteId: provisionalNoteId,
						userId: auth.userId || user_id,
						nameNote: name_note,
						editorJsonContent: tiptapDoc,
					});
					if (!persistedNote) {
						console.warn('initNewNoteSettings: initial save did not return persisted note id.');
					}
				} finally {
					creatingNoteIdRef.current = false;
				}
			}

			return tiptapDoc;
		} catch (error) {
			setIf_source_content_parsing_finished(false);
			throw error;
		} finally {
			setIf_expect_source_content_parsing(false);
			bootstrappingImportedNoteRef.current = false;
		}
	}, [
		setNoteSourceType,
		setEditorContentJsonSync,
		getEffectiveAuth,
		handleAuthFailure,
		user_id,
		name_note,
		current_note_type,
		navigate,
		incrementContentUploaded,
	]);

	const setNoteSourceFile = useCallback((fileOrFiles) => {
		const incomingFiles = toFileArray(fileOrFiles).filter((file) => isAllowedSourceFile(file));
		if (incomingFiles.length === 0) {
			return false;
		}

		const fileType = getSourceTypeFromFile(incomingFiles[0]);
		if (!fileType) {
			return false;
		}

		if (!incomingFiles.every((file) => getSourceTypeFromFile(file) === fileType)) {
			return false;
		}

		const currentFormData = note_source_form_data_ref.current;
		const existingFiles = toFileArray(currentFormData?.getAll?.('source_file')).filter((file) => file instanceof File);
		const existingType = existingFiles.length > 0 ? getSourceTypeFromFile(existingFiles[0]) : null;

		const shouldAppendToExisting = existingType === fileType;
		const files = shouldAppendToExisting ? [...existingFiles, ...incomingFiles] : incomingFiles;

		const formData = new FormData();
		files.forEach((file) => {
			formData.append('source_file', file, file.name);
		});

		note_source_form_data_ref.current = formData;
		setNote_source_form_data(formData);
		setNote_source_type(fileType);

		return true;
	}, []);

	const setNoteSourceLink = useCallback((link) => {
		if (typeof link !== 'string') {
			return false;
		}

		const trimmedLink = link.trim();
		if (!trimmedLink) {
			return false;
		}

		const formData = new FormData();
		formData.append('link', trimmedLink);

		const isYouTubeLink = /youtube\.com|youtu\.be/i.test(trimmedLink);
		note_source_form_data_ref.current = formData;
		setNote_source_form_data(formData);
		setNote_source_type(isYouTubeLink ? 'youtube' : 'link');

		return true;
	}, []);

	const clearNoteSourceFormData = useCallback(() => {
		const emptyFormData = new FormData();
		note_source_form_data_ref.current = emptyFormData;
		setNote_source_form_data(emptyFormData);
		setNote_source_type(null);
	}, []);

	const saveNoteContentToDatabase = useCallback(async (noteContentPayload) => {
		if (!noteContentPayload) {
			return false;
		}
		const intentAtStart = navigationIntentRef.current;
		const shouldShowExitSavingOverlay = Boolean(noteContentPayload?.showExitSavingOverlay);

		const auth = await getEffectiveAuth();
		if (!auth?.accessToken) {
			handleAuthFailure('Session expired. Please sign in again.');
			console.warn('saveNoteContentToDatabase skipped: missing authenticated access token.');
			return false;
		}

		setIf_saving_note_content(true);
		if (shouldShowExitSavingOverlay) {
			setIf_saving_note_content_on_exit(true);
		}
		try {
			const currentNoteId = currentNoteIdRef.current;
			const currentView = currentNoteViewRef.current;
			const currentType = currentNoteTypeRef.current;
			if (DEBUG_NOTE_PIPELINE) {
				console.log('[NotePipeline] save:start', {
					note_id: currentNoteId,
					current_note_view: currentView,
					noteType: noteContentPayload?.noteType,
					selectedMode: noteContentPayload?.selectedMode,
					user_id,
				});
			}

			const headers = {
				'Content-Type': 'application/json',
			};
			headers.Authorization = `Bearer ${auth.accessToken}`;
			const requestedNoteId = isUuidLike(noteContentPayload?.noteId)
				? String(noteContentPayload.noteId)
				: (isUuidLike(currentNoteId) ? String(currentNoteId) : null);
			const shouldPersistChatConversation = Object.prototype.hasOwnProperty.call(
				noteContentPayload || {},
				'chat_conversation_json'
			);

			const { showExitSavingOverlay: _ignoredExitOverlayFlag, ...payloadWithoutUiFlag } = noteContentPayload || {};
			const payloadToPersist = {
				...payloadWithoutUiFlag,
				...(requestedNoteId ? { noteId: requestedNoteId } : {}),
			};

			if (shouldPersistChatConversation) {
				payloadToPersist.chat_conversation_json = normalizeChatConversation(
					noteContentPayload?.chat_conversation_json
				);
			}

			const response = await fetch(`${BACKEND_BASE_URL}/api/note-content/save`, {
				method: 'POST',
				headers,
				body: JSON.stringify(payloadToPersist),
			});

			const payload = await response.json().catch(() => ({}));
			if (response.status === 401) {
				handleAuthFailure(String(payload?.error || 'Invalid or expired authentication token'));
				return false;
			}
			if (!response.ok || !payload?.success) {
				if (DEBUG_NOTE_PIPELINE) {
					console.log('[NotePipeline] save:failed', {
						status: response.status,
						payload,
					});
				}
				throw new Error(buildErrorMessage(payload, response.status, 'Failed to save note content'));
			}

			const persistedNote = payload?.data;
			if (DEBUG_NOTE_PIPELINE) {
				console.log('[NotePipeline] save:success', {
					requestedNoteId,
					returnedNoteId: persistedNote?.noteId || null,
					path: location.pathname,
				});
			}

			if (persistedNote?.noteId && persistedNote.noteId !== currentNoteId) {
				setNote_id(persistedNote.noteId);
				const routeState = getNoteRouteStateFromPathname(currentPathnameRef.current);
				const routeTargetsSameNote = routeState.noteId && String(routeState.noteId) === String(currentNoteId || requestedNoteId || '');
				const safeView = routeTargetsSameNote && routeState.view
					? routeState.view
					: currentView;
				const safeType = safeView === 'adapted'
					? normalizeNoteType(routeState.noteType || currentType)
					: currentType;
				const canonicalPath = getNotePathForView(safeView, persistedNote.noteId, safeType);
				if (intentAtStart === navigationIntentRef.current && currentPathnameRef.current !== canonicalPath) {
					navigate(canonicalPath, { replace: true });
				}
			}

			const resolvedPayloadForSignature = {
				...payloadToPersist,
				noteId: String(persistedNote?.noteId || payloadToPersist.noteId || currentNoteId || '').trim() || undefined,
			};
			lastSavedPayloadSignatureRef.current = buildNoteSaveSignature(resolvedPayloadForSignature);

			return persistedNote || true;
		} catch (error) {
			console.error('saveNoteContentToDatabase failed:', error);
			return false;
		} finally {
			setIf_saving_note_content(false);
			if (shouldShowExitSavingOverlay) {
				setIf_saving_note_content_on_exit(false);
			}
		}
	}, [getEffectiveAuth, handleAuthFailure, navigate, user_id]);

	const buildCurrentNoteSavePayload = useCallback(() => {
		const currentNoteId = String(currentNoteIdRef.current || note_id || '').trim();
		const normalizedGetterEditorContent = normalizeEditorDoc(
			editorContentGetterRef.current ? editorContentGetterRef.current() : null
		);
		const normalizedStateEditorContent = normalizeEditorDoc(EditorContent_json_tiptap);
		const normalizedLatestEditorContent = normalizeEditorDoc(latestEditorContentRef.current);
		// Getter is live (editor.getJSON()); state may lag by one render if the last
		// keystroke state update hasn't committed yet. Use getter first when available.
		const currentEditorContent = normalizedGetterEditorContent
			?? normalizedStateEditorContent
			?? normalizedLatestEditorContent;
		const currentView = String(currentNoteViewRef.current || current_note_view || 'editor').toLowerCase();
		const currentType = currentView === 'adapted' ? normalizeNoteType(currentNoteTypeRef.current || current_note_type) : normalizeNoteType(currentNoteTypeRef.current || current_note_type);
		const selectedMode = String(if_note_summarized_or_deep || 'deep').toLowerCase() === 'summarized' ? 'summarized' : 'deep';

		if (DEBUG_NOTE_PIPELINE) {
			console.log('[NotePipeline] buildPayload', {
				currentNoteId,
				getterResult: normalizedGetterEditorContent ? `doc(${normalizedGetterEditorContent.content?.length} nodes)` : null,
				stateResult: normalizedStateEditorContent ? `doc(${normalizedStateEditorContent.content?.length} nodes)` : null,
				refResult: normalizedLatestEditorContent ? `doc(${normalizedLatestEditorContent.content?.length} nodes)` : null,
				chosen: currentEditorContent ? `doc(${currentEditorContent.content?.length} nodes)` : null,
				getterRegistered: Boolean(editorContentGetterRef.current),
			});
		}

		if (!if_note_opened && !currentNoteId && !currentEditorContent && !name_note) {
			return null;
		}

		return {
			noteId: currentNoteId || undefined,
			userId: user_id,
			noteType: currentType,
			selectedMode,
			nameNote: name_note,
			...(currentEditorContent ? { editorJsonContent: currentEditorContent } : {}),
			// Always send the full note state so backend can persist a complete snapshot.
			visualMindMapTitles: Array.isArray(visualMindMapTitles) ? visualMindMapTitles : [],
			visualMindMapsJson: Array.isArray(visualMindMapsJson) ? visualMindMapsJson : [],
			visualMindMapTitlesSummarized: Array.isArray(visualMindMapTitlesSummarized) ? visualMindMapTitlesSummarized : [],
			visualMindMapsJsonSummarized: Array.isArray(visualMindMapsJsonSummarized) ? visualMindMapsJsonSummarized : [],
			audioBlockTitles: Array.isArray(audioBlockTitles) ? audioBlockTitles : [],
			audioBlockJson: Array.isArray(audioBlockJson) ? audioBlockJson : [],
			audioBlockTitlesSummarized: Array.isArray(audioBlockTitlesSummarized) ? audioBlockTitlesSummarized : [],
			audioBlockJsonSummarized: Array.isArray(audioBlockJsonSummarized) ? audioBlockJsonSummarized : [],
			list_content_smart_text: Array.isArray(list_content_smart_text) ? list_content_smart_text : [],
			list_title_smart_text: Array.isArray(list_title_smart_text) ? list_title_smart_text : [],
			list_content_smart_textSummarized: Array.isArray(list_content_smart_textSummarized) ? list_content_smart_textSummarized : [],
			list_title_smart_textSummarized: Array.isArray(list_title_smart_textSummarized) ? list_title_smart_textSummarized : [],
			listJson_content_flashCards: Array.isArray(listJson_content_flashCards) ? listJson_content_flashCards : [],
			listJson_content_flashCardsSummarized: Array.isArray(listJson_content_flashCardsSummarized) ? listJson_content_flashCardsSummarized : [],
			chat_conversation_json: normalizeChatConversation(chat_conversation_json),
		};
	}, [
		if_note_opened,
		name_note,
		user_id,
		EditorContent_json_tiptap,
		current_note_view,
		current_note_type,
		if_note_summarized_or_deep,
		chat_conversation_json,
		visualMindMapsJson,
		visualMindMapsJsonSummarized,
		visualMindMapTitles,
		visualMindMapTitlesSummarized,
		audioBlockJson,
		audioBlockJsonSummarized,
		audioBlockTitles,
		audioBlockTitlesSummarized,
		list_content_smart_text,
		list_content_smart_textSummarized,
		list_title_smart_text,
		list_title_smart_textSummarized,
		listJson_content_flashCards,
		listJson_content_flashCardsSummarized,
		note_id,
	]);

	const flushCurrentNoteToDatabase = useCallback(async () => {
		if (noteAutoSaveTimerRef.current) {
			clearTimeout(noteAutoSaveTimerRef.current);
			noteAutoSaveTimerRef.current = null;
		}

		// Sync the live editor content into the ref so buildCurrentNoteSavePayload
		// sees the absolute latest content even if the React state hasn't committed yet.
		const liveContent = editorContentGetterRef.current ? editorContentGetterRef.current() : null;
		if (liveContent) {
			latestEditorContentRef.current = liveContent;
		}

		const payload = buildCurrentNoteSavePayload();
		if (!payload) {
			return false;
		}

		return saveNoteContentToDatabase({
			...payload,
			showExitSavingOverlay: true,
		});
	}, [buildCurrentNoteSavePayload, saveNoteContentToDatabase]);

	const loadNoteContentFromDatabase = useCallback(async (targetNoteId) => {
		const sanitizedNoteId = String(targetNoteId || '').trim();
		if (!sanitizedNoteId) {
			return false;
		}
		const intentAtStart = navigationIntentRef.current;

		const auth = await getEffectiveAuth();
		if (!auth?.accessToken) {
			handleAuthFailure('Session expired. Please sign in again.');
			return false;
		}

		if (loadingNoteIdRef.current === sanitizedNoteId) {
			return false;
		}

		setIf_loading_note_content(true);
		loadingNoteIdRef.current = sanitizedNoteId;

		try {
			if (DEBUG_NOTE_PIPELINE) {
				console.log('[NotePipeline] load:start', {
					targetNoteId: sanitizedNoteId,
					currentNoteId: note_id,
					user_id,
					path: location.pathname,
				});
			}

			const response = await fetch(`${BACKEND_BASE_URL}/api/note-content/${encodeURIComponent(sanitizedNoteId)}`, {
				method: 'GET',
				cache: 'no-store',
				headers: {
					Authorization: `Bearer ${auth.accessToken}`,
					'Cache-Control': 'no-cache, no-store, max-age=0',
					Pragma: 'no-cache',
				},
			});

			const payload = await response.json().catch(() => ({}));
			if (response.status === 401) {
				handleAuthFailure(String(payload?.error || 'Invalid or expired authentication token'));
				return false;
			}
			if (!response.ok || !payload?.success || !payload?.data) {
				if (DEBUG_NOTE_PIPELINE) {
					console.log('[NotePipeline] load:failed', {
						status: response.status,
						payload,
					});
				}
				throw new Error(buildErrorMessage(payload, response.status, 'Failed to load note content'));
			}

			if (getNoteIdFromPathname(currentPathnameRef.current) !== sanitizedNoteId) {
				if (DEBUG_NOTE_PIPELINE) {
					console.log('[NotePipeline] load:stale-ignored', {
						requestedNoteId: sanitizedNoteId,
						currentPath: currentPathnameRef.current,
					});
				}
				return false;
			}

			const loaded = payload.data;
			if (DEBUG_NOTE_PIPELINE) {
				console.log('[NotePipeline] load:success', {
					requestedNoteId: sanitizedNoteId,
					loadedNoteId: loaded?.noteId || null,
					editorHasContent: Boolean(loaded?.editorJsonContent),
					path: location.pathname,
				});
			}
			const shouldApplyRouteDrivenNavigation = intentAtStart === navigationIntentRef.current;
			const fallbackView = String(currentNoteViewRef.current || '').toLowerCase() === 'adapted' ? 'adapted' : 'editor';
			const fallbackType = normalizeNoteType(currentNoteTypeRef.current);
			const routeState = getNoteRouteStateFromPathname(currentPathnameRef.current);
			const routeView = routeState.view || fallbackView;
			const routeNoteType = routeView === 'adapted'
				? normalizeNoteType(routeState.noteType || fallbackType)
				: fallbackType;

			setNote_id(String(loaded.noteId || sanitizedNoteId));
			const normalizedEditorDoc = normalizeEditorDoc(loaded.editorJsonContent) || createEmptyTipTapDoc();
			setEditorContentJsonSync(normalizedEditorDoc);
			setName_note(String(loaded.nameNote || ''));
			setVisualMindMapTitles(Array.isArray(loaded.visualMindMapTitles) ? loaded.visualMindMapTitles : []);
			setVisualMindMapsJson(Array.isArray(loaded.visualMindMapsJson) ? loaded.visualMindMapsJson : []);
			setVisualMindMapTitlesSummarized(Array.isArray(loaded.visualMindMapTitlesSummarized) ? loaded.visualMindMapTitlesSummarized : []);
			setVisualMindMapsJsonSummarized(Array.isArray(loaded.visualMindMapsJsonSummarized) ? loaded.visualMindMapsJsonSummarized : []);
			setAudioBlockTitles(Array.isArray(loaded.audioBlockTitles) ? loaded.audioBlockTitles : []);
			setAudioBlockJson(Array.isArray(loaded.audioBlockJson) ? loaded.audioBlockJson : []);
			setAudioBlockTitlesSummarized(Array.isArray(loaded.audioBlockTitlesSummarized) ? loaded.audioBlockTitlesSummarized : []);
			setAudioBlockJsonSummarized(Array.isArray(loaded.audioBlockJsonSummarized) ? loaded.audioBlockJsonSummarized : []);
			setList_content_smart_text(Array.isArray(loaded.list_content_smart_text) ? loaded.list_content_smart_text : []);
			setList_title_smart_text(Array.isArray(loaded.list_title_smart_text) ? loaded.list_title_smart_text : []);
			setList_content_smart_textSummarized(Array.isArray(loaded.list_content_smart_textSummarized) ? loaded.list_content_smart_textSummarized : []);
			setList_title_smart_textSummarized(Array.isArray(loaded.list_title_smart_textSummarized) ? loaded.list_title_smart_textSummarized : []);
			setListJson_content_flashCards(Array.isArray(loaded.listJson_content_flashCards) ? loaded.listJson_content_flashCards : []);
			setListJson_content_flashCardsSummarized(Array.isArray(loaded.listJson_content_flashCardsSummarized) ? loaded.listJson_content_flashCardsSummarized : []);
			const normalizedLoadedChatConversation = normalizeChatConversation(loaded.chat_conversation_json);
			setChat_conversation_json(normalizedLoadedChatConversation);
			lastSavedPayloadSignatureRef.current = buildNoteSaveSignature({
				noteId: String(loaded.noteId || sanitizedNoteId),
				userId: user_id,
				noteType: normalizeNoteType(currentNoteTypeRef.current || current_note_type),
				selectedMode: String(if_note_summarized_or_deep || 'deep').toLowerCase() === 'summarized' ? 'summarized' : 'deep',
				nameNote: String(loaded.nameNote || ''),
				editorJsonContent: normalizedEditorDoc,
				visualMindMapTitles: Array.isArray(loaded.visualMindMapTitles) ? loaded.visualMindMapTitles : [],
				visualMindMapsJson: Array.isArray(loaded.visualMindMapsJson) ? loaded.visualMindMapsJson : [],
				visualMindMapTitlesSummarized: Array.isArray(loaded.visualMindMapTitlesSummarized) ? loaded.visualMindMapTitlesSummarized : [],
				visualMindMapsJsonSummarized: Array.isArray(loaded.visualMindMapsJsonSummarized) ? loaded.visualMindMapsJsonSummarized : [],
				audioBlockTitles: Array.isArray(loaded.audioBlockTitles) ? loaded.audioBlockTitles : [],
				audioBlockJson: Array.isArray(loaded.audioBlockJson) ? loaded.audioBlockJson : [],
				audioBlockTitlesSummarized: Array.isArray(loaded.audioBlockTitlesSummarized) ? loaded.audioBlockTitlesSummarized : [],
				audioBlockJsonSummarized: Array.isArray(loaded.audioBlockJsonSummarized) ? loaded.audioBlockJsonSummarized : [],
				list_content_smart_text: Array.isArray(loaded.list_content_smart_text) ? loaded.list_content_smart_text : [],
				list_title_smart_text: Array.isArray(loaded.list_title_smart_text) ? loaded.list_title_smart_text : [],
				list_content_smart_textSummarized: Array.isArray(loaded.list_content_smart_textSummarized) ? loaded.list_content_smart_textSummarized : [],
				list_title_smart_textSummarized: Array.isArray(loaded.list_title_smart_textSummarized) ? loaded.list_title_smart_textSummarized : [],
				listJson_content_flashCards: Array.isArray(loaded.listJson_content_flashCards) ? loaded.listJson_content_flashCards : [],
				listJson_content_flashCardsSummarized: Array.isArray(loaded.listJson_content_flashCardsSummarized) ? loaded.listJson_content_flashCardsSummarized : [],
				chat_conversation_json: normalizedLoadedChatConversation,
			});
			setIf_note_opened(true);
			setIf_expect_source_content_parsing(false);
			setIf_source_content_parsing_finished(true);
			setIf_expect_note_content_adapting(false);
			setIf_note_content_adapting_finish(false);
			setNote_adapting_run_keys([]);
			lastLoadedNoteIdRef.current = sanitizedNoteId;
			const canonicalPath = getNotePathForView(routeView, sanitizedNoteId, routeNoteType);
			if (shouldApplyRouteDrivenNavigation && currentPathnameRef.current !== canonicalPath) {
				navigate(canonicalPath, { replace: true });
			}
			return true;
		} catch (error) {
			console.error('loadNoteContentFromDatabase failed:', error);
			return false;
		} finally {
			if (loadingNoteIdRef.current === sanitizedNoteId) {
				loadingNoteIdRef.current = null;
			}
			setIf_loading_note_content(false);
		}
	}, [getEffectiveAuth, handleAuthFailure, navigate, setEditorContentJsonSync]);

	const AdaptNote = useCallback(async (options = {}) => {
		const forceRegenerate = Boolean(options?.forceRegenerate);
		if (current_note_view !== 'adapted') {
			return;
		}

		const currentEditorContent = EditorContent_json_tiptap || latestEditorContentRef.current;
		let noteContentPayload = null;
		let runKey = '';

		try {
			if (!currentEditorContent || typeof currentEditorContent !== 'object') {
				setIf_expect_note_content_adapting(false);
				setVisualMindMapTitles(createEmptyMindMapTitles());
				setVisualMindMapTitlesSummarized(createEmptyMindMapTitles());
				setVisualMindMapsJson(createEmptyMindMapsJson());
				setVisualMindMapsJsonSummarized(createEmptyMindMapsJson());
				setAudioBlockTitles(createEmptyAudioBlockTitles());
				setAudioBlockTitlesSummarized(createEmptyAudioBlockTitles());
				setAudioBlockJson(createEmptyAudioBlockJson());
				setAudioBlockJsonSummarized(createEmptyAudioBlockJson());
				setList_content_smart_text([]);
				setList_content_smart_textSummarized([]);
				setList_title_smart_text([]);
				setList_title_smart_textSummarized([]);
				setListJson_content_flashCards([]);
				setListJson_content_flashCardsSummarized([]);
				setIf_note_content_adapting_finish(false);
				return;
			}

			const selectedMode = String(if_note_summarized_or_deep || 'deep').toLowerCase() === 'summarized'
				? 'summarized'
				: 'deep';
			const noteType = String(current_note_type || 'visual').toLowerCase();
			const isAudioType = noteType === 'audio';
			const isReadType = noteType === 'read';
			const isKinesthicType = noteType === 'kinesthic';
			const isReadDeepMode = selectedMode === 'deep' && !isAudioType && isReadType;
			const isReadSummarizedMode = selectedMode === 'summarized' && !isAudioType && isReadType;
			const adaptationSignature = stableStringify({
				noteType,
				selectedMode,
				tiptapJson: currentEditorContent,
			});
			if (!forceRegenerate && lastAdaptationSignature === adaptationSignature) {
				return;
			}
			lastAdaptationSignature = adaptationSignature;

			runKey = `${current_note_view}|${noteType}|${selectedMode}`;
			const hasCachedContent = hasCachedAdaptedContent({
				noteType,
				selectedMode,
				visualMindMapTitles,
				visualMindMapsJson,
				visualMindMapTitlesSummarized,
				visualMindMapsJsonSummarized,
				audioBlockTitles,
				audioBlockJson,
				audioBlockTitlesSummarized,
				audioBlockJsonSummarized,
				list_content_smart_text,
				list_title_smart_text,
				list_content_smart_textSummarized,
				list_title_smart_textSummarized,
				listJson_content_flashCards,
				listJson_content_flashCardsSummarized,
			});
			const cachedSignature = lastGeneratedSignatureByRunKeyRef.current.get(runKey);

			if (!forceRegenerate && hasCachedContent && (!cachedSignature || cachedSignature === adaptationSignature)) {
				if (!cachedSignature) {
					lastGeneratedSignatureByRunKeyRef.current.set(runKey, adaptationSignature);
				}
				setIf_note_content_adapting_finish(true);
				return;
			}

			const isSameSourceAsInFlight = adaptRunRef.current.inFlightByKey.get(runKey) === currentEditorContent;
			const isSameSourceAsCompleted = adaptRunRef.current.lastCompletedSourceByKey.get(runKey) === currentEditorContent;

			if (adaptRunRef.current.inFlightByKey.has(runKey) && isSameSourceAsInFlight) {
				return;
			}

			if (!forceRegenerate && !adaptRunRef.current.inFlightByKey.has(runKey) && isSameSourceAsCompleted) {
				return;
			}

			setIf_expect_note_content_adapting(true);
			setIf_note_content_adapting_finish(false);
			setNote_adapting_run_keys((previousKeys) => (
				previousKeys.includes(runKey) ? previousKeys : [...previousKeys, runKey]
			));

			adaptRunRef.current.inFlightByKey.set(runKey, currentEditorContent);

			const preGenerationPayload = buildCurrentNoteSavePayload();
			if (preGenerationPayload) {
				saveNoteContentToDatabase(preGenerationPayload).catch((error) => {
					console.error('Pre-generation save failed:', error);
				});
			}

			const endpoint = isReadDeepMode
				? `${BACKEND_BASE_URL}/api/ai/smart-text/deep`
				: isKinesthicType
					? `${BACKEND_BASE_URL}/api/ai/smart-flashcards/${selectedMode}`
				: isAudioType
					? (selectedMode === 'summarized'
						? `${BACKEND_BASE_URL}/api/ai/audio/summarized`
						: `${BACKEND_BASE_URL}/api/ai/audio/deep`)
				: isReadSummarizedMode
					? `${BACKEND_BASE_URL}/api/ai/smart-text/summarized`
					: (selectedMode === 'summarized'
						? `${BACKEND_BASE_URL}/api/ai/mindmap/summarized`
						: `${BACKEND_BASE_URL}/api/ai/mindmap/deep`);

			const response = await fetch(endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ tiptapJson: currentEditorContent }),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok || !payload?.data) {
				const fallbackErrorMessage = isReadDeepMode
					? 'Failed to adapt note to smart text'
					: isAudioType
						? 'Failed to adapt note to audio'
						: 'Failed to adapt note to mindmaps';

				throw new Error(buildErrorMessage(
					payload,
					response.status,
					fallbackErrorMessage
				));
			}

			if (isReadDeepMode || isReadSummarizedMode) {
				const nextSmartTextContents = Array.isArray(payload.data?.markdownTexts) ? payload.data.markdownTexts : [];
				const nextSmartTextTitles = Array.isArray(payload.data?.titles)
					? payload.data.titles
					: Array.isArray(payload.data?.finalMarkdownParts)
						? payload.data.finalMarkdownParts.map((part, index) => String(part?.title || `Partie ${index + 1}`))
						: [];

				if (isReadSummarizedMode) {
					setList_content_smart_textSummarized(nextSmartTextContents);
					setList_title_smart_textSummarized(nextSmartTextTitles);
					noteContentPayload = {
						userId: user_id,
						nameNote: name_note,
						...(currentEditorContent ? { editorJsonContent: currentEditorContent } : {}),
						noteType,
						selectedMode,
						list_content_smart_text: nextSmartTextContents,
						list_title_smart_text: nextSmartTextTitles,
					};
				} else {
					setList_content_smart_text(nextSmartTextContents);
					setList_title_smart_text(nextSmartTextTitles);
					noteContentPayload = {
						userId: user_id,
						nameNote: name_note,
						...(currentEditorContent ? { editorJsonContent: currentEditorContent } : {}),
						noteType,
						selectedMode,
						list_content_smart_text: nextSmartTextContents,
						list_title_smart_text: nextSmartTextTitles,
					};
				}
			} else if (isKinesthicType) {
				const nextFlashCardsJsonList = Array.isArray(payload.data)
					? payload.data
					: (payload.data && typeof payload.data === 'object' ? [payload.data] : []);

				if (selectedMode === 'summarized') {
					setListJson_content_flashCardsSummarized(nextFlashCardsJsonList);
				} else {
					setListJson_content_flashCards(nextFlashCardsJsonList);
				}
				noteContentPayload = {
					userId: user_id,
					nameNote: name_note,
					...(currentEditorContent ? { editorJsonContent: currentEditorContent } : {}),
					noteType,
					selectedMode,
					listJson_content_flashCards: nextFlashCardsJsonList,
				};
			} else if (isAudioType) {
				const nextAudioTitles = Array.isArray(payload.data?.titles) ? payload.data.titles : [];
				const nextAudioBlocks = Array.isArray(payload.data?.podcastAudioJsons) ? payload.data.podcastAudioJsons : [];

				if (selectedMode === 'summarized') {
					setAudioBlockTitlesSummarized(nextAudioTitles);
					setAudioBlockJsonSummarized(nextAudioBlocks);
					noteContentPayload = {
						userId: user_id,
						nameNote: name_note,
						...(currentEditorContent ? { editorJsonContent: currentEditorContent } : {}),
						noteType,
						selectedMode,
						audioBlockTitles: nextAudioTitles,
						audioBlockJson: nextAudioBlocks,
					};
				} else {
					setAudioBlockTitles(nextAudioTitles);
					setAudioBlockJson(nextAudioBlocks);
					noteContentPayload = {
						userId: user_id,
						nameNote: name_note,
						...(currentEditorContent ? { editorJsonContent: currentEditorContent } : {}),
						noteType,
						selectedMode,
						audioBlockTitles: nextAudioTitles,
						audioBlockJson: nextAudioBlocks,
					};
				}
			} else {
				const nextTitles = Array.isArray(payload.data?.titles) ? payload.data.titles : [];
				const nextMindMapsJson = Array.isArray(payload.data?.mapsJson) ? payload.data.mapsJson : [];

				if (selectedMode === 'summarized') {
					setVisualMindMapTitlesSummarized(nextTitles);
					setVisualMindMapsJsonSummarized(nextMindMapsJson);
					noteContentPayload = {
						userId: user_id,
						nameNote: name_note,
						...(currentEditorContent ? { editorJsonContent: currentEditorContent } : {}),
						noteType,
						selectedMode,
						visualMindMapTitles: nextTitles,
						visualMindMapsJson: nextMindMapsJson,
					};
				} else {
					setVisualMindMapTitles(nextTitles);
					setVisualMindMapsJson(nextMindMapsJson);
					noteContentPayload = {
						userId: user_id,
						nameNote: name_note,
						...(currentEditorContent ? { editorJsonContent: currentEditorContent } : {}),
						noteType,
						selectedMode,
						visualMindMapTitles: nextTitles,
						visualMindMapsJson: nextMindMapsJson,
					};
				}
			}

			incrementAdaptionsContent();
			setIf_note_content_adapting_finish(true);
			adaptRunRef.current.lastCompletedSourceByKey.set(runKey, currentEditorContent);
			lastGeneratedSignatureByRunKeyRef.current.set(runKey, adaptationSignature);
			if (noteContentPayload) {
				saveNoteContentToDatabase(noteContentPayload).catch((error) => {
					console.error('Post-generation save failed:', error);
				});
			}
		} catch (error) {
			setIf_note_content_adapting_finish(false);
			adaptRunRef.current.lastCompletedSourceByKey.delete(runKey);
			const selectedMode = String(if_note_summarized_or_deep || 'deep').toLowerCase() === 'summarized'
				? 'summarized'
				: 'deep';
			const noteType = String(current_note_type || 'visual').toLowerCase();
			runKey = `${current_note_view}|${noteType}|${selectedMode}`;
			lastGeneratedSignatureByRunKeyRef.current.delete(runKey);
			lastAdaptationSignature = null;
			throw error;
		} finally {
			adaptRunRef.current.inFlightByKey.delete(runKey);
			setNote_adapting_run_keys((previousKeys) => previousKeys.filter((key) => key !== runKey));
			setIf_expect_note_content_adapting(false);
		}
	}, [
		current_note_view,
		if_note_summarized_or_deep,
		current_note_type,
		user_id,
		name_note,
		saveNoteContentToDatabase,
		buildCurrentNoteSavePayload,
		visualMindMapTitles,
		visualMindMapsJson,
		visualMindMapTitlesSummarized,
		visualMindMapsJsonSummarized,
		audioBlockTitles,
		audioBlockJson,
		audioBlockTitlesSummarized,
		audioBlockJsonSummarized,
		list_content_smart_text,
		list_title_smart_text,
		list_content_smart_textSummarized,
		list_title_smart_textSummarized,
		listJson_content_flashCards,
		listJson_content_flashCardsSummarized,
		incrementAdaptionsContent,
	]);

	useEffect(() => {
		if (current_note_view !== 'adapted') {
			setIf_expect_note_content_adapting(false);
			setIf_note_content_adapting_finish(false);
			setNote_adapting_run_keys([]);
			lastAdaptationSignature = null;
		}
	}, [current_note_view]);

	useEffect(() => {
		const noteIdFromUrl = getNoteIdFromPathname(location.pathname);
		if (!noteIdFromUrl) {
			return;
		}

		const normalizedRouteNoteId = String(noteIdFromUrl).trim();
		const currentLoadedNoteId = String(currentNoteIdRef.current || '').trim();
		const lastLoadedNoteId = String(lastLoadedNoteIdRef.current || '').trim();
		const isAlreadyLoadedInContext = (
			(currentLoadedNoteId && normalizedRouteNoteId === currentLoadedNoteId)
			|| (lastLoadedNoteId && normalizedRouteNoteId === lastLoadedNoteId)
		);

		if (isAlreadyLoadedInContext && loadingNoteIdRef.current !== normalizedRouteNoteId) {
			return;
		}

		loadNoteContentFromDatabase(normalizedRouteNoteId);
	}, [location.pathname, loadNoteContentFromDatabase]);

	// Keep a stable ref so exit-save and beforeunload can always call the latest
	// version of buildCurrentNoteSavePayload without adding it to their dep arrays
	// (which would cause them to re-run on every keystroke).
	useEffect(() => {
		buildCurrentNoteSavePayloadRef.current = buildCurrentNoteSavePayload;
	}, [buildCurrentNoteSavePayload]);

	useEffect(() => {
		if (!if_note_opened || if_loading_note_content) {
			return undefined;
		}

		const snapshot = buildCurrentNoteSavePayload();
		const snapshotSignature = buildNoteSaveSignature(snapshot);
		if (!snapshot || !snapshotSignature || snapshotSignature === lastSavedPayloadSignatureRef.current) {
			return undefined;
		}

		if (noteAutoSaveTimerRef.current) {
			clearTimeout(noteAutoSaveTimerRef.current);
		}

		noteAutoSaveTimerRef.current = setTimeout(() => {
			noteAutoSaveTimerRef.current = null;
			const latestSnapshot = buildCurrentNoteSavePayload();
			const latestSignature = buildNoteSaveSignature(latestSnapshot);
			if (!latestSnapshot || !latestSignature || latestSignature === lastSavedPayloadSignatureRef.current) {
				return;
			}

			saveNoteContentToDatabase(latestSnapshot).catch((error) => {
				console.error('Auto-save note content failed:', error);
			});
		}, 600);

		return () => {
			if (noteAutoSaveTimerRef.current) {
				clearTimeout(noteAutoSaveTimerRef.current);
				noteAutoSaveTimerRef.current = null;
			}
		};
	}, [buildCurrentNoteSavePayload, if_note_opened, if_loading_note_content, saveNoteContentToDatabase]);

	useEffect(() => {
		const previousPathname = previousPathnameRef.current;
		const currentPathname = location.pathname;

		if (!hasMountedPathTrackingRef.current) {
			hasMountedPathTrackingRef.current = true;
			previousPathnameRef.current = currentPathname;
			return;
		}

		const previousRouteState = getNoteRouteStateFromPathname(previousPathname);
		const currentRouteState = getNoteRouteStateFromPathname(currentPathname);
		const hasExitedNoteView = Boolean(previousRouteState.view) && !Boolean(currentRouteState.view);

		if (hasExitedNoteView) {
			// Use the ref so we always get the latest payload function without
			// needing buildCurrentNoteSavePayload in this effect's dep array
			// (which would otherwise re-run this effect on every keystroke).
			const exitPayload = buildCurrentNoteSavePayloadRef.current?.();
			if (exitPayload) {
				saveNoteContentToDatabase(exitPayload).catch((error) => {
					console.error('Exit note view save failed:', error);
				});
			}
		}

		previousPathnameRef.current = currentPathname;
	}, [location.pathname, saveNoteContentToDatabase]);

	useEffect(() => {
		const handleBeforeUnload = () => {
			const payload = buildCurrentNoteSavePayloadRef.current?.();
			if (!payload) return;

			const token = accessTokenRef.current;
			if (!token) return;

			fetch(`${BACKEND_BASE_URL}/api/note-content/save`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify(payload),
				keepalive: true,
			});
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, []);


	const resetNoteState = useCallback(() => {
		if (noteAutoSaveTimerRef.current) {
			clearTimeout(noteAutoSaveTimerRef.current);
			noteAutoSaveTimerRef.current = null;
		}
		lastSavedPayloadSignatureRef.current = '';
		setEditorContentJsonSync(null);
		setNote_id(null);
		setVisualMindMapTitles(createEmptyMindMapTitles());
		setVisualMindMapsJson(createEmptyMindMapsJson());
		setName_note('');
		setIf_note_opened(false);
		setIf_expect_source_content_parsing(false);
		setIf_source_content_parsing_finished(false);
		setIf_loading_note_content(false);
		setIf_saving_note_content_on_exit(false);
		setIf_expect_note_content_adapting(false);
		setIf_note_content_adapting_finish(false);
		setNote_adapting_run_keys([]);
		setIf_note_summarized_or_deep('deep');
		setCurrent_note_view('editor');
		setCurrent_note_type(null);
		setAudioBlockTitles(createEmptyAudioBlockTitles());
		setAudioBlockJson(createEmptyAudioBlockJson());
		setList_content_smart_text([]);
		setList_content_smart_textSummarized([]);
		setList_title_smart_text([]);
		setList_title_smart_textSummarized([]);
		setListJson_content_flashCards([]);
		setListJson_content_flashCardsSummarized([]);
		setVisualMindMapTitlesSummarized(createEmptyMindMapTitles());
		setVisualMindMapsJsonSummarized(createEmptyMindMapsJson());
		setAudioBlockTitlesSummarized(createEmptyAudioBlockTitles());
		setAudioBlockJsonSummarized(createEmptyAudioBlockJson());
		adaptRunRef.current = {
			inFlightByKey: new Map(),
			lastCompletedSourceByKey: new Map(),
		};
		lastGeneratedSignatureByRunKeyRef.current.clear();
		lastLoadedNoteIdRef.current = null;
		setNote_source_type(null);
		const emptyFormData = new FormData();
		note_source_form_data_ref.current = emptyFormData;
		setNote_source_form_data(emptyFormData);
		setChat_conversation_json([]);
	}, [setEditorContentJsonSync]);

	const stateValue = useMemo(
		() => ({
			EditorContent_json_tiptap,
			note_id,
			visualMindMapTitles,
			visualMindMapTitlesSummarized,
			visualMindMapsJson,
			visualMindMapsJsonSummarized,
			name_note,
			if_note_opened,
			if_expect_source_content_parsing,
			if_source_content_parsing_finished,
			if_loading_note_content,
			if_saving_note_content,
			if_saving_note_content_on_exit,
			if_expect_note_content_adapting,
			if_note_content_adapting_finish,
			note_adapting_run_keys,
			if_note_summarized_or_deep,
			current_note_view,
			current_note_type,
			audioBlockTitles,
			audioBlockTitlesSummarized,
			audioBlockJson,
			audioBlockJsonSummarized,
			list_content_smart_text,
			list_content_smart_textSummarized,
			list_title_smart_text,
			list_title_smart_textSummarized,
			listJson_content_flashCards,
			listJson_content_flashCardsSummarized,
			note_source_type,
			note_source_form_data,
			chat_conversation_json,
		}),
		[
			EditorContent_json_tiptap,
			note_id,
			visualMindMapTitles,
			visualMindMapTitlesSummarized,
			visualMindMapsJson,
			visualMindMapsJsonSummarized,
			name_note,
			if_note_opened,
			if_expect_source_content_parsing,
			if_source_content_parsing_finished,
			if_loading_note_content,
			if_saving_note_content,
			if_saving_note_content_on_exit,
			if_expect_note_content_adapting,
			if_note_content_adapting_finish,
			note_adapting_run_keys,
			if_note_summarized_or_deep,
			current_note_view,
			current_note_type,
			audioBlockTitles,
			audioBlockTitlesSummarized,
			audioBlockJson,
			audioBlockJsonSummarized,
			list_content_smart_text,
			list_content_smart_textSummarized,
			list_title_smart_text,
			list_title_smart_textSummarized,
			listJson_content_flashCards,
			listJson_content_flashCardsSummarized,
			note_source_type,
			note_source_form_data,
			chat_conversation_json,
		]
	);

	const dispatchValue = useMemo(
		() => ({
			setEditorContent_json_tiptap: setEditorContentJsonSync,
			setNote_id,
			setVisualMindMapTitles,
			setVisualMindMapsJson,
			setName_note,
			setIf_note_opened,
			setIf_expect_source_content_parsing,
			setIf_source_content_parsing_finished,
			setIf_saving_note_content,
			setIf_saving_note_content_on_exit,
			setIf_expect_note_content_adapting,
			setIf_note_content_adapting_finish,
			setIf_note_summarized_or_deep,
			setCurrent_note_view,
			setCurrent_note_type,
			setAudioBlockTitles,
			setAudioBlockJson,
			setList_content_smart_text,
			setList_title_smart_text,
			setListJson_content_flashCards,
			setVisualMindMapTitlesSummarized,
			setVisualMindMapsJsonSummarized,
			setAudioBlockTitlesSummarized,
			setAudioBlockJsonSummarized,
			setList_content_smart_textSummarized,
			setList_title_smart_textSummarized,
			setListJson_content_flashCardsSummarized,
			setNoteSourceType,
			setNoteSourceFile,
			setNoteSourceLink,
			clearNoteSourceFormData,
			setChat_conversation_json,
			AdaptNote,
			initNewNoteSettings,
			flushCurrentNoteToDatabase,
			resetNoteState,
			registerEditorContentGetter,
		}),
		[
			AdaptNote,
			clearNoteSourceFormData,
			flushCurrentNoteToDatabase,
			initNewNoteSettings,
			registerEditorContentGetter,
			resetNoteState,
			setEditorContentJsonSync,
			setNoteSourceFile,
			setNoteSourceLink,
			setNoteSourceType,
		]
	);


	return (
		<NoteStateContext.Provider value={stateValue}>
			<NoteDispatchContext.Provider value={dispatchValue}>{children}</NoteDispatchContext.Provider>
		</NoteStateContext.Provider>
	);
};

export const useNoteState = () => {
	const context = useContext(NoteStateContext);

	if (!context) {
		throw new Error('useNoteState must be used within a NoteProvider.');
	}

	return context;
};

export const useNoteDispatch = () => {
	const context = useContext(NoteDispatchContext);

	if (!context) {
		throw new Error('useNoteDispatch must be used within a NoteProvider.');
	}

	return context;
};

export const useNoteContext = () => {
	const state = useNoteState();
	const dispatch = useNoteDispatch();

	return useMemo(() => ({ ...state, ...dispatch }), [state, dispatch]);
};

export default NoteStateContext;
